import React, {useCallback, useMemo, useState, useEffect, ReactNode} from 'react';
import {Input, Select, Button, Collapse} from 'antd';
import {EditOutlined} from '@ant-design/icons';
import {tid} from '../../../../../../testUtils';
import {
  ModelMetadata,
  useModelMetadataAssociations,
  useModelMetadataOptions,
  useModelMetadataOptionsLowerCase,
  modelMetadataOptionsLabels,
} from '../../../../../../store/modelMetadata';
import {ActionMenuProps} from '../ActionMenuTypes';
import {ModelMetadataOptionsDTOOptions} from 'hemwb-api';
import styles from './UpdateOptionForm.module.scss';

type UpdateOptionFormProps = {
  property: Required<ActionMenuProps>['property'];
  value?: string;
  submitting: boolean;
  onSubmit: (newValue: string, metadata?: ModelMetadata) => void;
  onCancel?: () => void;
  submitButtonText?: string;
  submitButtonIcon?: ReactNode;
  isAddMode?: boolean;
};

const UpdateOptionForm: React.FC<UpdateOptionFormProps> = ({
  property,
  value = '',
  onSubmit,
  onCancel,
  submitting,
  submitButtonText = 'Update Option',
  submitButtonIcon = <EditOutlined />,
  isAddMode = false,
}) => {
  const optionsLowerCase = useModelMetadataOptionsLowerCase();
  const options = useModelMetadataOptions();
  const associations = useModelMetadataAssociations();

  const [newValue, setNewValue] = useState(value);
  const [error, setError] = useState<string | null>(null);

  // Metadata state
  const [metadata, setMetadata] = useState<ModelMetadata>({});

  const ALPHA_NUMERIC_DASH_REGEX = /^[a-zA-Z]+$/;
  const isFileTypes = modelMetadataOptionsLabels[property] === 'File Types';
  const isStudyNumbers = property === 'studyNumbers';

  // Load existing metadata when component mounts or value changes
  useEffect(() => {
    if (isAddMode) {
      // Clear all form values when in Add mode (new option being created)
      setNewValue('');
      setError(null);
      setMetadata({});
    } else if (isStudyNumbers && associations?.[property]?.[value]) {
      // Load existing metadata when editing
      const existingMetadata = associations[property][value];
      setMetadata({
        disease: existingMetadata.disease || '',
        product: existingMetadata.product || '',
        therapeutic_area: existingMetadata.therapeutic_area || '',
        model_type: existingMetadata.model_type || '',
        type: existingMetadata.type || '',
        information_classification: existingMetadata.information_classification || '',
      });
    }
  }, [value, property, associations, isStudyNumbers, isAddMode]);

  const isSubmitDisabled = useMemo(() => {
    if (!newValue.trim()) return true;
    if (newValue === value) return true;
    if (submitting) return true;
    return false;
  }, [newValue, value, submitting]);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setNewValue(inputValue);
    setError(null);

    // Check uniqueness - optionsLowerCase is a map of MetadataOptionDTO objects
    const existingOptions = optionsLowerCase?.[property as keyof ModelMetadataOptionsDTOOptions] || [];
    const optionValues = Array.isArray(existingOptions)
      ? existingOptions.map((opt: any) => (typeof opt === 'string' ? opt : opt.value?.toLocaleLowerCase?.()))
      : [];

    if (inputValue !== value && optionValues.includes(inputValue.toLocaleLowerCase())) {
      setError('This option already exists');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isFileTypes && !ALPHA_NUMERIC_DASH_REGEX.test(e.key) && !['Backspace', 'Delete', 'Tab'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleMetadataChange = (key: string, value: any) => {
    setMetadata((prev) => ({
      ...prev,
      [key]: value || null,
    }));
  };

  const handleCancel = useCallback(() => {
    // Clear form values when Cancel is clicked
    setNewValue(value);
    setError(null);
    setMetadata({});
    if (onCancel) {
      onCancel();
    }
  }, [value, onCancel]);

  const handleSubmit = useCallback(() => {
    if (newValue.trim() && newValue !== value && !error) {
      // Clean up metadata - only include non-empty values
      const cleanedMetadata = Object.keys(metadata).reduce((acc, key) => {
        if (metadata[key as keyof ModelMetadata]) {
          acc[key as keyof ModelMetadata] = metadata[key as keyof ModelMetadata];
        }
        return acc;
      }, {} as ModelMetadata);

      onSubmit(newValue, Object.keys(cleanedMetadata).length > 0 ? cleanedMetadata : undefined);
    }
  }, [newValue, value, error, metadata, onSubmit]);

  const metadataItems = isStudyNumbers
    ? [
        {
          key: '1',
          label: 'Assigned Metadata',
          children: (
            <div className={styles.metadataFields}>
              <div className={styles.metadataField}>
                <label className={styles.metadataLabel}>Disease</label>
                <Select
                  value={metadata.disease || undefined}
                  onChange={(val) => handleMetadataChange('disease', val)}
                  options={
                    Array.isArray(options?.diseases)
                      ? options.diseases.filter((d) => !d.retired).map((d) => ({label: d.value, value: d.value}))
                      : []
                  }
                  placeholder="Select disease"
                  allowClear
                  disabled={submitting}
                  getPopupContainer={() => document.body}
                />
              </div>

              <div className={styles.metadataField}>
                <label className={styles.metadataLabel}>Product</label>
                <Select
                  value={metadata.product || undefined}
                  onChange={(val) => handleMetadataChange('product', val)}
                  options={
                    Array.isArray(options?.products)
                      ? options.products.filter((p) => !p.retired).map((p) => ({label: p.value, value: p.value}))
                      : []
                  }
                  placeholder="Select product"
                  allowClear
                  disabled={submitting}
                  showSearch
                  filterOption={(input, opt) =>
                    (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  getPopupContainer={() => document.body}
                />
              </div>

              <div className={styles.metadataField}>
                <label className={styles.metadataLabel}>Therapeutic Area</label>
                <Select
                  value={metadata.therapeutic_area || undefined}
                  onChange={(val) => handleMetadataChange('therapeutic_area', val)}
                  options={
                    Array.isArray(options?.therapeuticAreas)
                      ? options.therapeuticAreas
                          .filter((ta) => !ta.retired)
                          .map((ta) => ({label: ta.value, value: ta.value}))
                      : []
                  }
                  placeholder="Select therapeutic area"
                  allowClear
                  disabled={submitting}
                  getPopupContainer={() => document.body}
                />
              </div>

              <div className={styles.metadataField}>
                <label className={styles.metadataLabel}>Model Type</label>
                <Select
                  value={metadata.model_type || undefined}
                  onChange={(val) => handleMetadataChange('model_type', val)}
                  options={
                    Array.isArray(options?.modelTypes)
                      ? options.modelTypes.filter((mt) => !mt.retired).map((mt) => ({label: mt.value, value: mt.value}))
                      : []
                  }
                  placeholder="Select model type"
                  allowClear
                  disabled={submitting}
                  getPopupContainer={() => document.body}
                />
              </div>

              <div className={styles.metadataField}>
                <label className={styles.metadataLabel}>Type</label>
                <Select
                  value={metadata.type || undefined}
                  onChange={(val) => handleMetadataChange('type', val)}
                  options={[
                    {label: 'ExcelNative', value: 'ExcelNative'},
                    {label: 'Mathematica', value: 'Mathematica'},
                    {label: 'Python', value: 'Python'},
                    {label: 'R', value: 'R'},
                    {label: 'Other', value: 'Other'},
                  ]}
                  placeholder="Select type"
                  allowClear
                  disabled={submitting}
                  getPopupContainer={() => document.body}
                />
              </div>

              <div className={styles.metadataField}>
                <label className={styles.metadataLabel}>Information Classification</label>
                <Select
                  value={metadata.information_classification || undefined}
                  onChange={(val) => handleMetadataChange('information_classification', val)}
                  options={[
                    {label: 'Confidential', value: 'Confidential'},
                    {label: 'Proprietary', value: 'Proprietary'},
                    {label: 'Public', value: 'Public'},
                    {label: 'Sensitive', value: 'Sensitive'},
                  ]}
                  placeholder="Select classification"
                  allowClear
                  disabled={submitting}
                  getPopupContainer={() => document.body}
                />
              </div>
            </div>
          ),
        },
      ]
    : [];

  return (
    <div className={styles.formContainer}>
      <div className={styles.formGroup}>
        <label className={styles.label}>
          Option <span className={styles.required}>*</span>
        </label>
        <div className={styles.optionInput}>
          <Input
            value={newValue}
            onChange={handleValueChange}
            onKeyDown={handleKeyDown}
            placeholder="Enter option"
            disabled={submitting}
            maxLength={isFileTypes ? 25 : undefined}
            status={error ? 'error' : ''}
            {...tid('input', 'option-value')}
          />
        </div>
        {error && <div className={styles.errorText}>{error}</div>}
      </div>

      {metadataItems.length > 0 && (
        <div className={styles.metadataSection}>
          <Collapse items={metadataItems} />
        </div>
      )}

      <div className={styles.buttonsContainer}>
        <Button onClick={handleCancel} disabled={submitting} className={styles.cancelBtn}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={submitting}
          disabled={isSubmitDisabled}
          icon={submitButtonIcon}
          className={styles.submitBtn}
        >
          {submitButtonText}
        </Button>
      </div>
    </div>
  );
};

export default UpdateOptionForm;
